import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import checkIsValidEmail from 'utils/checkIsValidEmail';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';
import { DEFAULT_EMAIL_FRAME_LOGO } from 'constants/defaultConstants';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetCommunicationsSectionPermissions } from 'modules/communications/hooks/useGetCommunicationSectionPermissions';
import { useGetCommunications } from 'modules/communications/hooks/useGetCommunications';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import {
  deleteCommunication,
  updateCommunication,
} from 'modules/communications/redux/communicationActions';
import defaultSignature from 'modules/communications/constants/defaultSignature';
import SendCommunicationModal from 'modules/_shared/components/Modals/SendCommunicationModal';
import { SendTestEmailModalProvider } from 'modules/_shared/components/Modals/SendTestEmail/context/SendTestEmailModalContext';
import SendTestEmail from 'modules/_shared/components/Modals/SendTestEmail';
import { sections } from 'modules/communications/constants/sections';

import { getSocietyEmailLinkColor } from 'modules/society/utils';
import generateEmailSignature from 'modules/communications/utils/generateEmailSignature';
import { updateSociety } from 'modules/society/redux/societyActions';
import { isValidEmailLocalPart } from 'modules/communications/utils/isValidEmailLocalPart';
import { SendCommunicationModalProvider } from 'modules/_shared/components/Modals/SendCommunicationModal/context/SendCommunicationModalContext';
import { CommunicationsPageContextType } from '../types/CommunicationsPageContextType';
import { communicationsPageInitValue } from '../constants/communicationsPageInitValue';

const CommunicationsPageContext = createContext<CommunicationsPageContextType>(
  communicationsPageInitValue
);

export const CommunicationsPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslate();
  const { societyId, section, page } = useParams();

  const currentSection = section || sections.INBOX;
  const currentPage = page ? parseInt(page, 10) : 1;

  const history = useHistory();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const user = useSelector((state: State) => state.user);

  const communications = useSelector(
    (state: State) => state.communications || []
  );

  const societyCommunicationsConfig =
    actualSociety?.configuration?.communications;

  const emailSignature =
    societyCommunicationsConfig?.emailSignature || defaultSignature;

  const [senderEmailLocalPart, setSenderEmailLocalPart] = useState<string>(
    societyCommunicationsConfig?.senderEmail || ''
  );
  const [senderName, setSenderName] = useState<string>(
    societyCommunicationsConfig?.senderName || ''
  );

  const [replyToEmail, setReplyToEmail] = useState<string>(
    societyCommunicationsConfig?.replyEmail || ''
  );
  const [isValidReplyToEmail, setIsValidReplyToEmail] = useState<boolean>(
    checkIsValidEmail(societyCommunicationsConfig?.replyEmail || '')
  );
  const [hasReplyTo, setHasReplyTo] = useState<boolean>(
    !!societyCommunicationsConfig?.replyEnabled
  );
  const [signatureName, setSignatureName] = useState<string>(
    emailSignature?.name || ''
  );
  const [signatureJobTitle, setSignatureJobTitle] = useState<string>(
    emailSignature?.jobTitle || ''
  );
  const [signatureLogo] = useState<string>(
    actualSociety?.customization?.theme?.emailFrameLogo ||
      actualSociety?.customization?.theme?.logo ||
      DEFAULT_EMAIL_FRAME_LOGO
  );
  const [signatureWebsite, setSignatureWebsite] = useState<string>(
    emailSignature?.website || ''
  );
  const [signaturePhone, setSignaturePhone] = useState<string>(
    emailSignature?.phone || ''
  );
  const [signatureAddress, setSignatureAddress] = useState<string>(
    emailSignature?.address || ''
  );
  const [signatureFooter, setSignatureFooter] = useState<string>(
    emailSignature?.footer || ''
  );
  const [hasBeenModified, setHasBeenModified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { data: permissionsData } = useGetCommunicationsSectionPermissions();

  const { isLoading: isLoadingCommunications } = useGetCommunications({
    societyId,
    page: currentPage,
  });

  const handleChangePage = (newPage: number) => {
    history.push(`/comunicaciones/${societyId}/${currentSection}/${newPage}`);
  };

  const handleOpenCommunication = (index: number) => {
    const communication = communications[index];

    if (!communication) return;

    if (communication?._id && communication?.isUnread) {
      store.dispatch(
        updateCommunication({
          communicationId: communication._id,
          updateCommunicationData: { isUnread: false },
        })
      );
    }

    history.push(`/detalle-comunicacion/${societyId}/${communication?._id}`);
  };

  const handleToggleInboxSection = () => {
    history.push(`/comunicaciones/${societyId}/inbox/${currentPage}`);
  };

  const handleToggleSettingsSection = () => {
    history.push(`/comunicaciones/${societyId}/settings`);
  };
  const handleDeleteCommunication = (index: number) => {
    const communication = communications[index];

    if (!communication) return;

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelCommunication'),
      swalElementName: '',
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          setIsLoading(true);

          if (communication?._id) {
            await store.dispatch(deleteCommunication(communication?._id));
            successSwal(t('CommunicationDeletedSuccessfully'));
          }

          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          errorSwal(t('ErrorDeletingCommunication'));
        }
      },
    });
  };

  const handleNewCommunication = () => {
    if (permissionsData.isReadOnly) return;
    store.dispatch(
      setModal(
        <SendCommunicationModalProvider size={sizes.XL}>
          <SendCommunicationModal />
        </SendCommunicationModalProvider>
      )
    );
  };

  const handleChangeSenderEmailLocalPart = (emailLocalPart: string) => {
    if (isValidEmailLocalPart(emailLocalPart) || emailLocalPart === '') {
      setSenderEmailLocalPart(emailLocalPart);
      setHasBeenModified(true);
    }
  };

  const handleChangeSenderName = (name: string) => {
    setSenderName(name);
    setHasBeenModified(true);
  };

  const handleChangeReplyToEmail = (email: string) => {
    setReplyToEmail(email);
    const isValid = checkIsValidEmail(email);
    setIsValidReplyToEmail(isValid);
    setHasBeenModified(true);
  };

  const handleToggleHasReplyTo = () => {
    setHasReplyTo((prev) => !prev);
    setHasBeenModified(true);
  };

  const handleChangeSignatureName = (name: string) => {
    setSignatureName(name);
    setHasBeenModified(true);
  };

  const handleChangeSignatureJobTitle = (jobTitle: string) => {
    setSignatureJobTitle(jobTitle);
    setHasBeenModified(true);
  };

  const handleChangeSignatureWebsite = (website: string) => {
    setSignatureWebsite(website);
    setHasBeenModified(true);
  };

  const handleChangeSignaturePhone = (phone: string) => {
    setSignaturePhone(phone);
    setHasBeenModified(true);
  };

  const handleChangeSignatureAddress = (address: string) => {
    setSignatureAddress(address);
    setHasBeenModified(true);
  };

  const handleChangeSignatureFooter = (footer: string) => {
    setSignatureFooter(footer);
    setHasBeenModified(true);
  };

  const handleSendTestEmail = () => {
    const DEFAULT_SUBJECT = `✉️ ${actualSociety?.name} te ha enviado un mail de prueba!`;
    const DEFAULT_BODY = `Hola, este es un email enviado desde ${actualSociety?.name} para que puedas validar la configuración de tus comunicaciones`;

    const emailData = {
      senderEmailLocalPart,
      senderName,
      emailSignatureHTML: generateEmailSignature({
        signature: {
          name: signatureName,
          jobTitle: signatureJobTitle,
          logo: signatureLogo,
          website: signatureWebsite,
          phone: signaturePhone,
          address: signatureAddress,
          footer: signatureFooter,
        },
        linkColor: getSocietyEmailLinkColor(actualSociety),
      }),
      subject: DEFAULT_SUBJECT,
      template: 'sttok-general',
      body: DEFAULT_BODY,
      user: user['_id'],
      society: actualSociety?.['_id'],
      date: new Date(),
      status: 'SENT',
    };

    store.dispatch(
      setModal(
        <SendTestEmailModalProvider emailData={emailData}>
          <SendTestEmail />
        </SendTestEmailModalProvider>
      )
    );
  };

  const handleSaveSettings = async () => {
    try {
      if (permissionsData.isReadOnly || !permissionsData.isAdmin) return;

      setIsLoading(true);

      const updatedCommunicationsConfiguration = {
        ...actualSociety?.configuration?.communications,
        senderEmail: senderEmailLocalPart,
        senderName,
        replyEnabled: hasReplyTo,
        replyEmail: replyToEmail,
        emailSignature: {
          name: signatureName,
          jobTitle: signatureJobTitle,
          logo: signatureLogo,
          website: signatureWebsite,
          phone: signaturePhone,
          address: signatureAddress,
          footer: signatureFooter,
        },
        emailSignatureHTML: generateEmailSignature({
          signature: {
            name: signatureName,
            jobTitle: signatureJobTitle,
            logo: signatureLogo,
            website: signatureWebsite,
            phone: signaturePhone,
            address: signatureAddress,
            footer: signatureFooter,
          },
          linkColor: getSocietyEmailLinkColor(actualSociety),
        }),
      };

      const updatedConfiguration = {
        ...actualSociety?.configuration,
        communications: updatedCommunicationsConfiguration,
      };

      await store.dispatch(
        updateSociety(
          actualSociety?._id,
          { configuration: updatedConfiguration },
          false
        )
      );

      setIsLoading(false);

      setHasBeenModified(false);

      successSwal(t('CommunicationSettingsUptadeSuccessfully'));
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorUpdatingCommunicationSettings'));
    }
  };

  return (
    <CommunicationsPageContext.Provider
      value={{
        data: {
          societyName: actualSociety?.name || '',
          page: currentPage,
          totalCommunications: actualSociety?.communicationsCount?.total || 0,
          totalUnreadCommunications:
            actualSociety?.communicationsCount?.unread || 0,
          communications,
          hasSignatureView:
            (actualSociety?.configuration?.hasWhiteBranding &&
              actualSociety?.configuration?.communications
                ?.emailSignatureEnabled) ||
            false,
          senderEmailLocalPart,
          senderDomain:
            actualSociety?.configuration?.communications?.senderDomain ||
            '@sttok.com',
          senderName,
          replyToEmail,
          isValidReplyToEmail,
          hasReplyTo,
          signatureName,
          signatureJobTitle,
          signatureLogo,
          signatureWebsite,
          signaturePhone,
          signatureAddress,
          signatureFooter,
          linkColor: getSocietyEmailLinkColor(actualSociety),
          section: currentSection,
          hasBeenModified,
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading: isLoadingCommunications || isLoading,
        },
        actions: {
          handleChangePage,
          handleToggleInboxSection,
          handleToggleSettingsSection,
          handleNewCommunication,
          handleOpenCommunication,
          handleDeleteCommunication,
          handleChangeSenderEmailLocalPart,
          handleChangeSenderName,
          handleChangeReplyToEmail,
          handleToggleHasReplyTo,
          handleChangeSignatureName,
          handleChangeSignatureJobTitle,
          handleChangeSignatureWebsite,
          handleChangeSignaturePhone,
          handleChangeSignatureAddress,
          handleChangeSignatureFooter,
          handleSendTestEmail,
          handleSaveSettings,
        },
      }}
    >
      {children}
    </CommunicationsPageContext.Provider>
  );
};

export const useCommunicationsPage = () => {
  const context = useContext(CommunicationsPageContext);
  if (!context) {
    throw new Error(
      'useCommunicationsPage must be used within a CommunicationsPageProvider'
    );
  }
  return context;
};
